import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { mockAssessments } from '@/mocks/assessments';
import { mockChildren } from '@/mocks/children';

const riskColors: Record<string, string> = {
  critical: 'text-red-600 bg-red-50 border-red-200',
  high: 'text-orange-600 bg-orange-50 border-orange-200',
  moderate: 'text-amber-600 bg-amber-50 border-amber-200',
  low: 'text-green-600 bg-green-50 border-green-200',
};

const riskBarColor: Record<string, string> = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  moderate: 'bg-amber-500',
  low: 'bg-green-500',
};

export default function RiskAssessmentPage() {
  const navigate = useNavigate();
  const [selectedAssessment, setSelectedAssessment] = useState(mockAssessments[0]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [selectedChild, setSelectedChild] = useState('');
  const [scores, setScores] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const totalScore = selectedAssessment.categories.reduce((sum, c) => sum + c.score, 0);
  const maxScore = selectedAssessment.categories.reduce((sum, c) => sum + c.maxScore, 0);
  const riskColor = selectedAssessment.riskLevel === 'critical' ? '#ef4444' : selectedAssessment.riskLevel === 'high' ? '#f97316' : selectedAssessment.riskLevel === 'moderate' ? '#f59e0b' : '#22c55e';

  const handleScoreChange = (catName: string, val: number) => {
    setScores((prev) => ({ ...prev, [catName]: val }));
  };

  const handleSubmitNew = () => {
    setSubmitted(true);
    setTimeout(() => { setSubmitted(false); setShowNewForm(false); setSelectedChild(''); setScores({}); setNotes(''); }, 2000);
  };

  return (
    <AppLayout title="Risk Assessment" breadcrumb={['Home', 'Risk Assessment']}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Assessment List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 text-sm">Assessments</h3>
              <button onClick={() => setShowNewForm(true)} className="text-xs bg-teal-500 text-white px-3 py-1.5 rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
                <i className="ri-add-line" />New
              </button>
            </div>
            <div className="divide-y divide-gray-50">
              {mockAssessments.map((a) => (
                <div
                  key={a.id}
                  onClick={() => { setSelectedAssessment(a); setShowNewForm(false); }}
                  className={`px-5 py-4 cursor-pointer transition-colors ${selectedAssessment.id === a.id ? 'bg-teal-50 border-l-4 border-teal-500' : 'hover:bg-gray-50 border-l-4 border-transparent'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{a.childName}</p>
                      <p className="text-xs font-mono text-gray-400">{a.caseNumber}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{a.assessmentDate}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-lg font-bold text-gray-900">{a.totalScore}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium capitalize ${riskColors[a.riskLevel]}`}>{a.riskLevel}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-gray-100">
              <button onClick={() => navigate('/child-profiles')} className="text-xs text-teal-500 hover:text-teal-700 cursor-pointer whitespace-nowrap">View all child profiles →</button>
            </div>
          </div>
        </div>

        {/* Right: Assessment Detail or New Form */}
        <div className="lg:col-span-2">
          {showNewForm ? (
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-gray-800">New Risk Assessment</h3>
                <button onClick={() => setShowNewForm(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
              </div>
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-checkbox-circle-line text-teal-500 text-3xl" />
                  </div>
                  <p className="font-semibold text-gray-800">Assessment Submitted!</p>
                  <p className="text-sm text-gray-500 mt-1">Submitted for supervisor review.</p>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">Select Child <span className="text-red-500">*</span></label>
                    <select value={selectedChild} onChange={(e) => setSelectedChild(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500">
                      <option value="">Choose a child profile...</option>
                      {mockChildren.map((c) => (
                        <option key={c.id} value={c.id}>{c.firstName} {c.lastName} — {c.caseNumber}</option>
                      ))}
                    </select>
                  </div>
                  {['Physical Safety', 'Emotional Well-being', 'Family Environment', 'Educational Status'].map((cat) => (
                    <div key={cat} className="mb-4 bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-semibold text-gray-800">{cat}</p>
                        <span className="text-sm font-bold text-teal-600">{scores[cat] || 0}/25</span>
                      </div>
                      <input
                        type="range" min={0} max={25} value={scores[cat] || 0}
                        onChange={(e) => handleScoreChange(cat, Number(e.target.value))}
                        className="w-full accent-teal-500"
                      />
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>Low Risk (0)</span><span>Critical (25)</span>
                      </div>
                    </div>
                  ))}
                  <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">Assessment Notes</label>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Enter assessment notes and recommendations..." rows={4} maxLength={500} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500 resize-none" />
                  </div>
                  <div className="flex gap-3 justify-end">
                    <button onClick={() => setShowNewForm(false)} className="text-sm border border-gray-200 px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap">Cancel</button>
                    <button onClick={handleSubmitNew} className="text-sm bg-teal-500 text-white px-6 py-2 rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap">Submit for Review</button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Score Overview */}
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <div className="flex flex-col md:flex-row items-start gap-6">
                  <div className="text-center flex-shrink-0">
                    <div className="relative w-32 h-32 mx-auto">
                      <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f3f4f6" strokeWidth="3" />
                        <circle cx="18" cy="18" r="15.9" fill="none" stroke={riskColor} strokeWidth="3"
                          strokeDasharray={`${(totalScore / maxScore) * 100} ${100 - (totalScore / maxScore) * 100}`} strokeLinecap="round" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-3xl font-bold text-gray-900">{totalScore}</span>
                        <span className="text-xs text-gray-400">/{maxScore}</span>
                      </div>
                    </div>
                    <span className={`mt-2 inline-block text-sm px-3 py-1 rounded-full border font-semibold capitalize ${riskColors[selectedAssessment.riskLevel]}`}>{selectedAssessment.riskLevel} Risk</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-bold text-gray-900">{selectedAssessment.childName}</h3>
                        <p className="text-xs font-mono text-gray-400">{selectedAssessment.caseNumber}</p>
                        <p className="text-xs text-gray-400 mt-0.5">Assessed by {selectedAssessment.assessorName} · {selectedAssessment.assessmentDate}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${selectedAssessment.status === 'approved' ? 'bg-green-100 text-green-700' : selectedAssessment.status === 'submitted' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                        {selectedAssessment.status}
                      </span>
                    </div>
                    <div className="space-y-2 mt-4">
                      {selectedAssessment.categories.map((cat) => (
                        <div key={cat.name}>
                          <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span>{cat.name}</span>
                            <span className="font-semibold">{cat.score}/{cat.maxScore}</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${riskBarColor[selectedAssessment.riskLevel]}`}
                              style={{ width: `${(cat.score / cat.maxScore) * 100}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Category Details */}
              {selectedAssessment.categories.map((cat) => (
                <div key={cat.name} className="bg-white rounded-xl border border-gray-100 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-800 text-sm">{cat.name}</h4>
                    <span className="text-sm font-bold text-gray-700">{cat.score}/{cat.maxScore}</span>
                  </div>
                  <div className="space-y-2">
                    {cat.indicators.map((ind, i) => (
                      <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white ${ind.value >= 4 ? 'bg-red-500' : ind.value >= 3 ? 'bg-amber-500' : 'bg-green-500'}`}>
                          {ind.value}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-700">{ind.label}</p>
                          {ind.notes && <p className="text-xs text-gray-400 mt-0.5">{ind.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Recommendations */}
              <div className="bg-white rounded-xl border border-amber-200 p-5">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><i className="ri-lightbulb-line text-amber-500" />Recommended Actions</h4>
                <ul className="space-y-2">
                  {selectedAssessment.recommendations.map((rec, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-arrow-right-s-line text-teal-500 mt-0.5 flex-shrink-0" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Historical Trend */}
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h4 className="font-semibold text-gray-800 mb-4 text-sm">Risk Score History</h4>
                <div className="flex items-end gap-3 h-20">
                  {selectedAssessment.previousScores.map((ps, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs font-bold text-gray-700">{ps.score}</span>
                      <div className="w-full rounded-t-md bg-teal-500 transition-all" style={{ height: `${(ps.score / 100) * 60}px` }} />
                      <span className="text-xs text-gray-400 text-center leading-tight">{ps.date.slice(5)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Supervisor Notes */}
              {selectedAssessment.supervisorNotes && (
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h4 className="font-semibold text-gray-800 mb-2 text-sm flex items-center gap-2"><i className="ri-user-star-line text-amber-500" />Supervisor Notes</h4>
                  <p className="text-sm text-gray-700">{selectedAssessment.supervisorNotes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
